import { Box, BoxProps, Stack, Typography } from '@mui/material';
import { cyan, indigo, purple } from '@mui/material/colors';
import { compact, flatten, some } from 'lodash';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';

import { Detection, DetectionClassification, useDetections } from '@/api/historicalData';
import { useSplBreachDetections } from '@/api/historicalData/useSplBreachDetections';
import { usePreferredTimeZone } from '@/api/userAccount';
import { Timeline, TimelineChart, drawTimelineChart } from '@/charts/timelineChart';
import { Timestamp } from '@/components/data/Timestamp';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useMapUiContext } from '@/context/MapUiContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { minorTextGrey } from '@/theme';
import { consolidateIntervals } from '@/utils';

export const DetectionTimeline = ({ sx }: Pick<BoxProps, 'sx'>) => {
    return (
        <Box sx={{ ...sx }}>
            <DetectionTimelineChart />
        </Box>
    );
};

const BAND_HEIGHT = 26;

const BASE_TIMELINE_LINE = {
    valueKey: 'density',
    valueLabel: 'Detection density',
    valueRange: { valueMin: 0, valueMax: 100 },
} as const;

const TIMELINES: {
    classifications: DetectionClassification[];
    line: Timeline<Detection>;
}[] = [
    {
        classifications: ['vessels', 'boats'],
        line: {
            id: 'vessels',
            seriesLabel: 'Vessels',
            color: cyan[800],
            ...BASE_TIMELINE_LINE,
        },
    },
    {
        classifications: ['dolphins'],
        line: {
            id: 'dolphins',
            seriesLabel: 'Dolphins',
            color: indigo[700],
            ...BASE_TIMELINE_LINE,
        },
    },
    {
        classifications: ['splBreach'],
        line: {
            id: 'spl',
            seriesLabel: 'SPL',
            color: purple[500],
            ...BASE_TIMELINE_LINE,
        },
    },
];

// just trying to make Detection look like a GenericObject for the benefit of chart typing
type GenericDetection = Omit<Detection, 'breachedSensorAlert'>;

export const DetectionTimelineChart = () => {
    const { selectedTimestamp, setSelectedTimestamp } = useTimelineNavigationContext();
    const timeZone = usePreferredTimeZone();
    const { dateRange } = useDateRangeContext();
    const { spotterIdsInViewport } = useMapUiContext();

    const containerId = 'detections-map-timeline' + useId();
    const chartRef = useRef<TimelineChart<GenericDetection> | null>(null);
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;

    const { data: splBreachesPerSpotter } = useSplBreachDetections({
        spotterIds: spotterIdsInViewport as string[],
        dateRange,
    });

    const { data: detectionsPerSpotter } = useDetections({
        spotterIds: spotterIdsInViewport as string[],
        dateRange,
    });

    const detections = useMemo(() => compact(flatten(detectionsPerSpotter)), [detectionsPerSpotter]);
    const splBreaches = useMemo(() => compact(flatten(splBreachesPerSpotter)), [splBreachesPerSpotter]);

    const dataset = useMemo(() => {
        return detections.concat(splBreaches);
    }, [detections, splBreaches]);

    // Once a line has been visible, we include it forever to reduce flickering as you pan around.
    const allLineIds = useRef(new Set<string>());

    const containerRef = useRef<HTMLElement>(null);
    const { width } = useElementSize(containerRef);
    const drawChart = useCallback(drawTimelineChart, []);

    const lines: Timeline<GenericDetection>[] = useMemo(() => {
        return compact(
            TIMELINES.map(({ line, classifications }) => {
                if (
                    !allLineIds.current.has(line.id) &&
                    !some(dataset, (datum) => classifications.includes(datum.classification))
                )
                    return null;
                allLineIds.current.add(line.id);

                const timeResolution = some(
                    dataset,
                    (datum) => classifications.includes(datum.classification) && datum.time_resolution === 'high'
                )
                    ? 'high'
                    : 'low';

                const observationIntervals = dataset
                    ? consolidateIntervals({
                          dataset,
                          dateRangeForDatum: ({ start_time, end_time }) => ({
                              startDate: new Date(start_time),
                              endDate: new Date(end_time),
                          }),
                          // not reusing the line's dataFilter because this needs to include non-detections (is_detection: false)
                          includeInterval: (datum) =>
                              classifications.includes(datum.classification) &&
                              datum.time_resolution === timeResolution,
                      })
                    : [];

                return {
                    ...line,
                    valueKey: undefined, // clear the valueKey and the renderer will turn binary - for this chart we just want "detection or not"
                    observationIntervals,
                    dataFilter: (datum: Detection) =>
                        classifications.includes(datum.classification) &&
                        datum.time_resolution === timeResolution &&
                        datum.is_detection,
                };
            })
        );
    }, [dataset]);

    const chartHeight = BAND_HEIGHT * (lines.length + 1);

    useEffect(() => {
        const newChart = drawChart({
            legend: false,
            selectTimeBetweenData: true,
            extendCursorThroughAxis: true,
            margin: { top: 0, bottom: BAND_HEIGHT, left: 0, right: 0 },
            dotHeight: 8,
            dataset: dataset as GenericDetection[],
            dateRange,
            xAxisKey: 'start_time',
            xAxisEndKey: 'end_time',
            containerId,
            chartWidth: width,
            chartHeight,
            lines,
            timeZone,
            onSelectTimestamp: setSelectedTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);
        setSelectedTimestamp(selectedTimestampRef.current, newChart.allTimestamps);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [dataset, drawChart, containerId, width, timeZone, setSelectedTimestamp, lines, dateRange, chartHeight]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    return (
        <Stack direction="row">
            <Stack
                sx={{ borderColor: 'divider', borderWidth: '1px', borderLeftStyle: 'solid', borderRightStyle: 'solid' }}
            >
                {lines.map(({ id, seriesLabel, color }) => (
                    <Typography
                        key={id}
                        variant="h6"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: `calc(${BAND_HEIGHT}px - 2px)`,
                            whiteSpace: 'nowrap',
                            color,
                            backgroundColor: `${color}19`,
                            px: 1,
                            my: '1px',
                        }}
                    >
                        {seriesLabel}
                    </Typography>
                ))}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'end',
                        flexGrow: 1,
                        px: 1,
                        mb: '-1px', // getting aligned with axis labels
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Timestamp
                        sx={{
                            color: minorTextGrey,
                            fontSize: '12px', // matching axis labels from d3 charts
                        }}
                        unit="date"
                        type="medium"
                        value={dateRange?.startDate}
                        fallback=""
                    />
                </Box>
            </Stack>
            <Box ref={containerRef} id={containerId} sx={{ height: chartHeight, flexGrow: 1 }} />
        </Stack>
    );
};
